import type { RoomView } from "@grundbesitz/shared";
import { botLabel, fieldName, formatMoney } from "../../lib/format";
import { isSpectating, ownedGroups, playerRows } from "../../lib/gameView";
import { GroupSwatch } from "../board/GroupBadge";
import { HotelIcon, HouseIcon } from "../board/BuildingIcons";
import { TokenIcon } from "../board/TokenIcon";
import { Button } from "../ui/Button";
import { Sheet } from "../ui/Sheet";
import { useUiStore } from "./uiStore";

function Buildings({ count }: { count: number }) {
  if (count === 0) return null;
  if (count === 5) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-ink-2">
        <HotelIcon size={16} />
        Hotel
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-xs text-ink-2">
      {Array.from({ length: count }, (_, i) => (
        <HouseIcon key={i} size={14} />
      ))}
      <span className="ml-1">{count === 1 ? "1 Haus" : `${count} Häuser`}</span>
    </span>
  );
}

/** Read-only view of another player's holdings, with the shortcut to offer a trade. */
export function PlayerAssetsSheet({ room }: { room: RoomView }) {
  const inspected = useUiStore((s) => s.inspectedPlayer);
  const inspectPlayer = useUiStore((s) => s.inspectPlayer);
  const openTradeWith = useUiStore((s) => s.openTradeWith);
  const game = room.game;
  const row = game && inspected ? playerRows(room).find((r) => r.player.id === inspected) : undefined;
  if (!game || !row) return null;

  const { player } = row;
  const groups = ownedGroups(game, player.id);
  const canTrade = !isSpectating(room) && !row.isSelf && !player.eliminated;

  return (
    <Sheet open onClose={() => inspectPlayer(null)} title={`Besitz von ${player.name}`}>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <TokenIcon token={player.token} color={player.color} size={44} title={player.name} />
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold">
              {player.name}
              {row.isBot ? ` (${botLabel(row.member?.botLevel)})` : ""}
            </p>
            <p className="text-sm text-ink-2 tabular-nums">
              Bargeld {formatMoney(player.cash)} · Vermögen {formatMoney(row.netWorth)}
            </p>
          </div>
        </div>

        {player.jailCards.length > 0 && (
          <p className="rounded-lg bg-surface-2 px-3 py-2 text-sm">
            {player.jailCards.length === 1 ? "Besitzt eine Freikarte fürs Gefängnis." : `Besitzt ${player.jailCards.length} Freikarten fürs Gefängnis.`}
          </p>
        )}

        {groups.length === 0 ? (
          <p className="text-sm text-ink-2">{player.eliminated ? "Ausgeschieden, ohne Besitz." : "Noch keine Grundstücke."}</p>
        ) : (
          <ul className="space-y-3">
            {groups.map((g) => (
              <li key={g.key}>
                <p className="mb-1 flex items-center gap-2 text-sm font-semibold">
                  {g.group && <GroupSwatch group={g.group} size={14} />}
                  {g.label}
                </p>
                <ul className="divide-y divide-line rounded-lg border border-line">
                  {g.fields.map((i) => {
                    const prop = game.properties[i];
                    return (
                      <li key={i} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                        <span className={prop.mortgaged ? "text-ink-2 line-through" : ""}>{fieldName(i)}</span>
                        <span className="flex items-center gap-2">
                          {prop.mortgaged && <span className="rounded-full border border-warn px-2 py-0.5 text-xs font-semibold text-warn">Hypothek</span>}
                          <Buildings count={prop.buildings} />
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}

        {canTrade && (
          <Button variant="primary" className="w-full" onClick={() => openTradeWith(player.id)}>
            Handel anbieten
          </Button>
        )}
      </div>
    </Sheet>
  );
}
